import { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function Requests() {
  const { hasPermission } = useAuth();
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    category: 'IT Support',
    description: ''
  });
  const [responseData, setResponseData] = useState({
    status: '',
    adminResponse: ''
  });

  const isAdmin = hasPermission('requests', 'update');

  const fetchRequests = useCallback(async () => {
    try {
      const response = await api.get('/requests');
      setRequests(response.data);
      
      // Mark responded requests as viewed by employee
      if (!isAdmin) {
        response.data.forEach(async (request) => {
          if ((request.status === 'resolved' || request.status === 'rejected') && !request.viewedByEmployee) {
            try {
              await api.post(`/requests/${request._id}/view`);
            } catch (err) {
              // Silently fail
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRequests();
    console.log('isAdmin (requests update permission):', isAdmin);
  }, [isAdmin, fetchRequests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/requests', formData);
      setShowModal(false);
      setFormData({
        subject: '',
        category: 'IT Support',
        description: ''
      });
      fetchRequests();
    } catch (error) {
      alert('Error creating request');
    }
  };

  const handleRespond = (request) => {
    setSelectedRequest(request);
    setResponseData({
      status: request.status,
      adminResponse: request.adminResponse || ''
    });
    setShowResponseModal(true);
  };

  const handleResponseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/requests/${selectedRequest._id}`, responseData);
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseData({ status: '', adminResponse: '' });
      fetchRequests();
    } catch (error) {
      alert('Error responding to request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      'in-progress': 'badge-info',
      resolved: 'badge-success',
      rejected: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  return (
    <div className="container">
      <div className="flex-between mb-20">
        <h1>Requests</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Submit Request
        </button>
      </div>

      {isAdmin && (
        <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
          <h3 style={{ marginBottom: '10px' }}>Requests Overview</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Pending</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                {requests.filter(r => r.status === 'pending').length}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>In Progress</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                {requests.filter(r => r.status === 'in-progress').length}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Resolved</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                {requests.filter(r => r.status === 'resolved').length}
              </p>
            </div>
            <div>
              <p style={{ color: '#666', fontSize: '14px' }}>Total</p>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
                {requests.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              {isAdmin && <th>Employee</th>}
              <th>Subject</th>
              <th>Category</th>
              <th>Description</th>
              <th>Status</th>
              <th>Submitted</th>
              {isAdmin && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request._id}>
                {isAdmin && (
                  <td>
                    {request.employee?.firstName} {request.employee?.lastName}
                    <br />
                    <small style={{ color: '#666' }}>({request.employee?.employeeId})</small>
                  </td>
                )}
                <td style={{ fontWeight: '500' }}>{request.subject}</td>
                <td>{request.category}</td>
                <td style={{ maxWidth: '300px' }}>
                  {request.description.length > 100 
                    ? `${request.description.substring(0, 100)}...` 
                    : request.description}
                </td>
                <td>
                  <span className={`badge ${getStatusBadge(request.status)}`}>
                    {request.status}
                  </span>
                  {request.adminResponse && (
                    <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                      <strong>Response:</strong> {request.adminResponse.substring(0, 50)}
                      {request.adminResponse.length > 50 && '...'}
                    </div>
                  )}
                </td>
                <td style={{ fontSize: '12px', color: '#666' }}>
                  {new Date(request.createdAt).toLocaleDateString()}
                </td>
                {isAdmin && (
                  <td>
                    <button 
                      className="btn btn-primary" 
                      onClick={() => handleRespond(request)}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      Respond
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        
        {requests.length === 0 && (
          <p className="text-center" style={{ padding: '20px' }}>No requests available</p>
        )}
      </div>

      {/* Submit Request Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Submit Request</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Brief subject of your request"
                />
              </div>
              
              <div className="form-group">
                <label>Category</label>
                <select
                  className="form-control"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="IT Support">IT Support</option>
                  <option value="HR Query">HR Query</option>
                  <option value="Finance">Finance</option>
                  <option value="Facilities">Facilities</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Detailed description of your request"
                  rows="5"
                />
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Response Modal */}
      {showResponseModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Respond to Request</h2>
              <button className="close-btn" onClick={() => setShowResponseModal(false)}>&times;</button>
            </div>
            
            <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
              <p><strong>Subject:</strong> {selectedRequest.subject}</p>
              <p><strong>Category:</strong> {selectedRequest.category}</p>
              <p><strong>Description:</strong> {selectedRequest.description}</p>
            </div>
            
            <form onSubmit={handleResponseSubmit}>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-control"
                  value={responseData.status}
                  onChange={(e) => setResponseData({ ...responseData, status: e.target.value })}
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="in-progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Admin Response</label>
                <textarea
                  className="form-control"
                  value={responseData.adminResponse}
                  onChange={(e) => setResponseData({ ...responseData, adminResponse: e.target.value })}
                  required
                  placeholder="Provide your response to the employee"
                  rows="5"
                />
              </div>
              
              <div className="flex-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowResponseModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Send Response
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Requests;
